import { login, register } from '../auth.js';
import { DEFAULT_PLAN_ID, PLANS, getPlanById } from '../plans.js';
import { getAuthCopy } from '../i18n.js';

const html = String.raw;

const visiblePlans = PLANS.filter((plan) => plan.visibleInStore !== false);

const formatTemplate = (template, replacements = {}) => {
  if (!template) return '';
  return template.replace(/{{(.*?)}}/g, (_, key) => {
    const trimmed = key.trim();
    const value = replacements[trimmed];
    return value === undefined || value === null ? '' : value;
  });
};

const planSelector = (state, copy) => {
  const selectedPlan = getPlanById(state.planId) ?? getPlanById(DEFAULT_PLAN_ID);
  const planName = selectedPlan ? selectedPlan.name : copy.planSelector.unknownPlan;
  return html`
    <section class="stack" style="gap:12px;">
      <h3>${copy.planSelector.title}</h3>
      <p class="muted small">${copy.planSelector.subtitle}</p>
      <div class="plan-grid">
        ${visiblePlans
          .map((plan) => {
            const isSelected = plan.id === state.planId;
            return html`
              <div class="plan-card ${isSelected ? 'selected' : ''}" data-plan-id="${plan.id}">
                <div class="pill" style="align-self:flex-start;">${plan.name}</div>
                <span class="price">${plan.priceLabel}</span>
                <p class="muted" style="margin:0;">${plan.description}</p>
                <button type="button" data-action="select-plan" data-plan-id="${plan.id}" ${isSelected ? 'disabled' : ''}>
                  ${isSelected ? copy.planSelector.selectedButton : copy.planSelector.selectButton}
                </button>
              </div>
            `;
          })
          .join('')}
      </div>
      <p class="muted small">${formatTemplate(copy.planSelector.currentLabel, { plan: planName })}</p>
    </section>
  `;
};

const profileFields = (state, copy) => html`
  <div class="stack" style="gap:12px;">
    <h3 style="margin-bottom:0;">${copy.sections?.profile ?? 'Persönliche Angaben'}</h3>
    <div class="row">
      <label style="flex:1;">
        ${copy.labels.firstName}
        <input type="text" name="firstName" value="${state.firstName}" autocomplete="given-name" required />
      </label>
      <label style="flex:1;">
        ${copy.labels.lastName}
        <input type="text" name="lastName" value="${state.lastName}" autocomplete="family-name" required />
      </label>
    </div>
    <label>
      ${copy.labels.company}
      <input type="text" name="company" value="${state.company}" placeholder="${copy.placeholders.company}" autocomplete="organization" />
    </label>
    <label>
      ${copy.labels.phone}
      <input type="tel" name="phone" value="${state.phone}" placeholder="${copy.placeholders.phone}" required autocomplete="tel" />
    </label>
    <label>
      ${copy.labels.vatNumber}
      <input type="text" name="vatNumber" value="${state.vatNumber}" placeholder="${copy.placeholders.vatNumber}" autocomplete="off" />
    </label>
    <div class="row">
      <label style="flex:1;">
        ${copy.labels.street}
        <input type="text" name="street" value="${state.street}" placeholder="${copy.placeholders.street}" autocomplete="address-line1" required />
      </label>
      <label style="flex:0.5;">
        ${copy.labels.postalCode}
        <input type="text" name="postalCode" value="${state.postalCode}" placeholder="${copy.placeholders.postalCode}" autocomplete="postal-code" required />
      </label>
      <label style="flex:1;">
        ${copy.labels.city}
        <input type="text" name="city" value="${state.city}" placeholder="${copy.placeholders.city}" autocomplete="address-level2" required />
      </label>
    </div>
    <label>
      ${copy.labels.country}
      <input type="text" name="country" value="${state.country}" placeholder="${copy.placeholders.country}" autocomplete="country-name" required />
    </label>
  </div>
`;

const formTemplate = (state, copy) => {
  const isLogin = state.mode === 'login';
  const modeKey = isLogin ? 'login' : 'register';
  return html`
    <div class="auth-view">
      <div class="card stack">
        <h2>${copy.titles[modeKey]}</h2>
        <p class="muted">${copy.descriptions[modeKey]}</p>
        <p class="muted small">${copy.requiredNote}</p>
        <form class="stack" id="auth-form">
          <label>
            ${copy.labels.email}
            <input type="email" name="email" value="${state.email}" required autocomplete="email" />
          </label>
          <label>
            ${copy.labels.password}
            <input type="password" name="password" value="${state.password}" required autocomplete="${isLogin ? 'current-password' : 'new-password'}" minlength="6" />
          </label>
          ${isLogin
            ? html`
                <p class="muted small" style="margin:0;">
                  <a href="#/forgot-password">${copy.forgotLink ?? 'Passwort vergessen?'}</a>
                </p>
              `
            : ''}
          ${isLogin
            ? ''
            : html`
                <input type="hidden" name="planId" value="${state.planId}" />
                ${planSelector(state, copy)}
                ${profileFields(state, copy)}
              `}
          <button type="submit">${copy.buttons.submit[modeKey]}</button>
          <p class="muted" id="auth-status"></p>
        </form>
        <p class="muted">
          ${copy.toggle[modeKey].question}
          <button type="button" id="toggle-mode" class="link-button">${copy.toggle[modeKey].action}</button>
        </p>
      </div>
    </div>
  `;
};

export const renderAuthView = (container, options = {}) => {
  const initialMode = options.initialMode === 'register' ? 'register' : 'login';
  const hashParams = new URLSearchParams(window.location.hash.split('?')[1] ?? '');
  const requestedPlanId = hashParams.get('plan') || hashParams.get('planId');
  const initialPlanId = getPlanById(requestedPlanId) ? requestedPlanId : DEFAULT_PLAN_ID;
  const state = {
    mode: initialMode,
    email: '',
    password: '',
    planId: initialPlanId,
    firstName: '',
    lastName: '',
    company: '',
    phone: '',
    vatNumber: '',
    street: '',
    postalCode: '',
    city: '',
    country: '',
  };

  const syncFormState = (form) => {
    const data = new FormData(form);
    state.email = data.get('email')?.toString() ?? '';
    state.password = data.get('password')?.toString() ?? '';
    const formPlan = data.get('planId')?.toString();
    if (formPlan) {
      state.planId = formPlan;
    }
    state.firstName = data.get('firstName')?.toString() ?? '';
    state.lastName = data.get('lastName')?.toString() ?? '';
    state.company = data.get('company')?.toString() ?? '';
    state.phone = data.get('phone')?.toString() ?? '';
    state.vatNumber = data.get('vatNumber')?.toString() ?? '';
    state.street = data.get('street')?.toString() ?? '';
    state.postalCode = data.get('postalCode')?.toString() ?? '';
    state.city = data.get('city')?.toString() ?? '';
    state.country = data.get('country')?.toString() ?? '';
  };

  const render = () => {
    const copy = getAuthCopy();
    container.innerHTML = formTemplate(state, copy);
    const form = container.querySelector('#auth-form');
    const statusEl = container.querySelector('#auth-status');
    const toggle = container.querySelector('#toggle-mode');

    if (!form || !statusEl || !toggle) {
      return;
    }

    if (state.mode === 'register') {
      form.querySelectorAll('[data-action="select-plan"]').forEach((button) => {
        button.addEventListener('click', (event) => {
          const planId = event.currentTarget.getAttribute('data-plan-id');
          if (!planId || planId === state.planId) {
            return;
          }
          state.planId = planId;
          render();
        });
      });
    }

    form.addEventListener('input', () => syncFormState(form));

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      syncFormState(form);
      const {
        email,
        password,
        planId,
        firstName,
        lastName,
        company,
        phone,
        vatNumber,
        street,
        postalCode,
        city,
        country,
      } = state;
      const copy = getAuthCopy();
      statusEl.textContent = state.mode === 'login' ? copy.statuses.checkingCredentials : copy.statuses.creatingAccount;
      try {
        if (state.mode === 'login') {
          await login({ email, password });
        } else {
          await register({
            email,
            password,
            planId,
            firstName,
            lastName,
            company,
            phone,
            vatNumber,
            street,
            postalCode,
            city,
            country,
          });
        }
        statusEl.textContent = copy.statuses.successRedirect;
        setTimeout(() => {
          window.location.hash = '#/dashboard';
        }, 400);
      } catch (error) {
        console.error(error);
        statusEl.textContent = error instanceof Error ? error.message : copy.statuses.genericError;
      }
    });

    toggle.addEventListener('click', () => {
      syncFormState(form);
      state.mode = state.mode === 'login' ? 'register' : 'login';
      render();
    });

  };

  render();
};
